// Welle 134 (15.10.2026): Kontoanalyse – Dashboard über die DB-1-Tabelle `kontoanalyse` (täglicher Kontoabruf).
// Zeitraum frei wählbar (Schnellwahl oder Von/Bis); Kontostand an einem Tag = letzter Abruf an oder vor diesem Tag.
// Differenz = Stand am Bis-Tag − Stand am Von-Tag (immer gerechnet, die Blatt-Spalte „Diff-Vortag“ wird nicht benutzt).
// Status und Kontoview je Konto = letzter bekannter Wert. Tabellen wie Excel: sortieren, Spaltenfilter, Summen, CSV.
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "../api/config";
import "./KontoanalysePage.css";

const API = `${API_BASE}/database/kontoanalyse`;
const eur = (v) => (v == null ? "–" : v.toLocaleString("de-DE", { style: "currency", currency: "EUR" }));
const eur0 = (v) => (v == null ? "–" : v.toLocaleString("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }));
const dDe = (t) => (t ? `${t.slice(8, 10)}.${t.slice(5, 7)}.${t.slice(0, 4)}` : "–");
const isoMinus = (t, tage) => { const d = new Date(t + "T12:00:00"); d.setDate(d.getDate() - tage); return d.toISOString().slice(0, 10); };
const vz = (v) => (v == null || Math.abs(v) < 0.005 ? "" : v > 0 ? "plus" : "minus");
const merke = (k, v) => { try { localStorage.setItem("kontoanalyse." + k, JSON.stringify(v)); } catch (e) { /* egal */ } };
const gemerkt = (k, std) => { try { const v = JSON.parse(localStorage.getItem("kontoanalyse." + k)); return v ?? std; } catch (e) { return std; } };

// Hotfix 6.23.1: Filter-Ausdrücke in JEDEM Eingabefeld gleich — „!=“ = ungleich (z. B. „!=0“, „!=aktiv“),
// „null“ bzw. „leer“ = leerer Wert („!=null“ = nicht leer), Zahlen mit < > <= >= = (z. B. „<0“), „=text“ = genau gleich.
const LEER = (v) => v == null || String(v).trim() === "";
function passt(v, ausdruck, zahl = false, anzeige) {
  let f = String(ausdruck || "").trim();
  if (!f) return true;
  let nicht = false;
  if (f.startsWith("!=")) { nicht = true; f = f.slice(2).trim(); }
  if (!f) return true;
  let ok;
  if (/^(null|leer)$/i.test(f)) ok = LEER(v);
  else if (zahl) {
    const m = f.match(/^(<=|>=|<|>|=)?\s*(-?[\d.,]+)$/);
    if (!m) ok = String(v ?? "").includes(f);
    else if (v == null) ok = false;
    else {
      const n = parseFloat(m[2].replace(/\./g, "").replace(",", ".")), op = m[1] || "=";
      ok = op === ">" ? v > n : op === ">=" ? v >= n : op === "<" ? v < n : op === "<=" ? v <= n : Math.abs(v - n) < 0.005;
    }
  } else {
    const t = String(anzeige ?? v ?? "").toLowerCase();
    ok = f.startsWith("=") ? t === f.slice(1).trim().toLowerCase() : t.includes(f.toLowerCase());
  }
  return nicht ? !ok : ok;
}
// Suche über mehrere Felder: jedes Wort muss passen; „!=…“ gilt für alle Felder (kein Feld enthält es), sonst für irgendeins
const suchePasst = (felder, q) => q.split(/\s+/).filter(Boolean).every((w) =>
  w.startsWith("!=") ? felder.every((f) => passt(f, w)) : felder.some((f) => passt(f, w)));

const SPALTEN = [
  { k: "ktoname", l: "Konto (Kurz)" }, { k: "kontostatus", l: "Status" }, { k: "kontoview", l: "View" },
  { k: "von", l: "Stand Von", zahl: true }, { k: "bis", l: "Stand Bis", zahl: true }, { k: "diff", l: "Differenz", zahl: true },
  { k: "min", l: "Tiefststand", zahl: true },
  { k: "kontoname", l: "Kontoname" }, { k: "inhaber", l: "Inhaber" }, { k: "iban", l: "IBAN" }, { k: "letzter", l: "letzter Abruf" },
];

function Verlauf({ punkte }) {
  if (punkte.length < 2) return <div className="ka-leer">Für eine Kurve braucht es mindestens zwei Abruftage im Zeitraum.</div>;
  const W = 900, H = 200, P = 46;
  const ys = punkte.map((p) => p.s), min = Math.min(...ys), max = Math.max(...ys), sp = max - min || 1;
  const x = (i) => P + (i * (W - P - 10)) / (punkte.length - 1), y = (v) => 10 + (H - 40) * (1 - (v - min) / sp);
  const pfad = punkte.map((p, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(p.s).toFixed(1)}`).join(" ");
  const ticks = [min, (min + max) / 2, max];
  const jeder = Math.max(1, Math.ceil(punkte.length / 8));
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="ka-chart" role="img" aria-label="Verlauf Summe Kontostand">
      {ticks.map((t, i) => (
        <g key={i}><line x1={P} x2={W - 10} y1={y(t)} y2={y(t)} className="ka-gitter" /><text x={P - 6} y={y(t) + 4} textAnchor="end" className="ka-achse">{(t / 1000).toLocaleString("de-DE", { maximumFractionDigits: 0 })}k</text></g>
      ))}
      <path d={`${pfad} L${x(punkte.length - 1)},${H - 30} L${x(0)},${H - 30} Z`} className="ka-flaeche" />
      <path d={pfad} className="ka-linie" />
      {punkte.map((p, i) => (
        <g key={p.t}>
          <circle cx={x(i)} cy={y(p.s)} r="3" className="ka-punkt"><title>{dDe(p.t)}: {eur(p.s)}</title></circle>
          {i % jeder === 0 && <text x={x(i)} y={H - 12} textAnchor="middle" className="ka-achse">{dDe(p.t).slice(0, 6)}</text>}
        </g>
      ))}
    </svg>
  );
}

export default function KontoanalysePage() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [fehler, setFehler] = useState("");
  const [von, setVon] = useState(""); const [bis, setBis] = useState("");
  const [status, setStatus] = useState(() => gemerkt("status", ["Aktiv"]));
  const [views, setViews] = useState(() => gemerkt("views", []));
  const [q, setQ] = useState("");
  const [colF, setColF] = useState({});
  const [sort, setSort] = useState({ k: "diff", auf: true });
  const [ansicht, setAnsicht] = useState(() => gemerkt("ansicht", "konten"));
  const [offen, setOffen] = useState(null);
  const [schnell, setSchnell] = useState("vortag");

  useEffect(() => {
    fetch(`${API}/daten`, { headers: { "X-MA-Token": localStorage.getItem("maToken") || "" } })
      .then(async (r) => { const j = await r.json(); if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`); return j; })
      .then((j) => { setD(j); const t = j.tage; if (t.length) { setBis(t[t.length - 1]); setVon(t[Math.max(0, t.length - 2)]); } })
      .catch((e) => setFehler(String(e.message || e)));
  }, []);
  useEffect(() => merke("status", status), [status]);
  useEffect(() => merke("views", views), [views]);
  useEffect(() => merke("ansicht", ansicht), [ansicht]);

  const tage = d?.tage || [];
  const erster = tage[0], letzter = tage[tage.length - 1];
  const zeitraum = (art) => {
    if (!letzter) return;
    setSchnell(art);
    const vorher = (t) => [...tage].reverse().find((x) => x <= t) || erster;
    const b = letzter;
    let v = b;
    if (art === "vortag") v = tage[Math.max(0, tage.length - 2)];
    else if (art === "woche") v = vorher(isoMinus(b, 7));
    else if (art === "30") v = vorher(isoMinus(b, 30));
    else if (art === "monat") v = vorher(isoMinus(b.slice(0, 8) + "01", 1));
    else if (art === "vormonat") { const e = isoMinus(b.slice(0, 8) + "01", 1); setVon(vorher(isoMinus(e.slice(0, 8) + "01", 1))); setBis(vorher(e)); return; }
    else if (art === "jahr") v = vorher(isoMinus(b.slice(0, 4) + "-01-01", 1));
    else if (art === "alles") v = erster;
    setVon(v); setBis(b);
  };

  // Stände je Konto, sortiert nach Tag
  const jeKonto = useMemo(() => {
    const m = {};
    (d?.staende || []).forEach((s) => { (m[s.k] = m[s.k] || []).push(s); });
    return m;
  }, [d]);
  const standAm = (k, t) => { const l = jeKonto[k] || []; let r = null; for (const s of l) { if (s.t <= t) r = s; else break; } return r; };

  const zaehle = (f) => { const c = {}; (d?.konten || []).forEach((k) => { c[k[f]] = (c[k[f]] || 0) + 1; }); return Object.entries(c).sort((a, b) => b[1] - a[1]); };
  const statusListe = useMemo(() => zaehle("kontostatus"), [d]); // eslint-disable-line react-hooks/exhaustive-deps
  const viewListe = useMemo(() => zaehle("kontoview"), [d]); // eslint-disable-line react-hooks/exhaustive-deps

  const konten = useMemo(() => {
    if (!d || !von || !bis) return [];
    return d.konten
      .filter((k) => (!status.length || status.includes(k.kontostatus)) && (!views.length || views.includes(k.kontoview)))
      .filter((k) => suchePasst([k.ktoname, k.kontoname, k.inhaber, k.iban, k.kontostatus, k.kontoview], q))
      .map((k) => {
        const a = standAm(k.schluessel, von), b = standAm(k.schluessel, bis);
        const imZeitraum = (jeKonto[k.schluessel] || []).filter((s) => s.t >= von && s.t <= bis && s.s != null);
        const l = jeKonto[k.schluessel] || [];
        return { ...k, von: a?.s ?? null, bis: b?.s ?? null, diff: a?.s != null && b?.s != null ? b.s - a.s : null,
                 min: imZeitraum.length ? Math.min(...imZeitraum.map((s) => s.s)) : null, letzter: l.length ? l[l.length - 1].t : null,
                 vonTag: a?.t, bisTag: b?.t };
      });
  }, [d, von, bis, status, views, q, jeKonto]); // eslint-disable-line react-hooks/exhaustive-deps

  const gefiltert = useMemo(() => {
    let l = konten.filter((r) => SPALTEN.every(({ k, zahl }) => passt(r[k], colF[k], zahl, k === "letzter" ? (r[k] ? dDe(r[k]) : "") : undefined)));
    const { k, auf } = sort;
    l = [...l].sort((x, y) => { const a = x[k], b = y[k]; if (a == null) return 1; if (b == null) return -1; return (typeof a === "number" ? a - b : String(a).localeCompare(String(b), "de")) * (auf ? 1 : -1); });
    return l;
  }, [konten, colF, sort]);

  const summe = (f, l = gefiltert) => l.reduce((s, r) => s + (r[f] ?? 0), 0);
  const zu = gefiltert.filter((r) => r.diff > 0.005), ab = gefiltert.filter((r) => r.diff < -0.005);
  const jeView = useMemo(() => {
    const m = {};
    gefiltert.forEach((r) => { const g = (m[r.kontoview] = m[r.kontoview] || { view: r.kontoview, n: 0, bis: 0, diff: 0 }); g.n++; g.bis += r.bis ?? 0; g.diff += r.diff ?? 0; });
    return Object.values(m).sort((a, b) => b.bis - a.bis);
  }, [gefiltert]);
  const maxDiff = Math.max(1, ...jeView.map((g) => Math.abs(g.diff)));

  const verlauf = useMemo(() => {
    const ks = gefiltert.map((r) => r.schluessel);
    return tage.filter((t) => t >= von && t <= bis).map((t) => ({ t, s: ks.reduce((s, k) => s + (standAm(k, t)?.s ?? 0), 0) }));
  }, [gefiltert, tage, von, bis]); // eslint-disable-line react-hooks/exhaustive-deps

  const roh = useMemo(() => {
    if (ansicht !== "tage") return [];
    const out = [];
    gefiltert.forEach((k) => {
      let vorher = standAm(k.schluessel, isoMinus(von, 1));
      (jeKonto[k.schluessel] || []).filter((s) => s.t >= von && s.t <= bis).forEach((s) => {
        out.push({ t: s.t, ktoname: k.ktoname, kontoname: k.kontoname, iban: k.iban, kontostatus: k.kontostatus, kontoview: k.kontoview, s: s.s,
                   d: vorher?.s != null && s.s != null ? s.s - vorher.s : null });
        vorher = s;
      });
    });
    return out.sort((a, b) => (a.t < b.t ? 1 : a.t > b.t ? -1 : String(a.ktoname).localeCompare(String(b.ktoname), "de")));
  }, [ansicht, gefiltert, von, bis, jeKonto]); // eslint-disable-line react-hooks/exhaustive-deps

  const umschalten = (liste, setListe, w) => setListe(liste.includes(w) ? liste.filter((x) => x !== w) : [...liste, w]);
  const csv = () => {
    const kopf = ansicht === "tage" ? ["Tag", "Konto (Kurz)", "Kontoname", "IBAN", "Status", "View", "Kontostand", "Diff. Vorabruf"]
      : SPALTEN.map((s) => s.l);
    const z = ansicht === "tage" ? roh.map((r) => [dDe(r.t), r.ktoname, r.kontoname, r.iban, r.kontostatus, r.kontoview, r.s, r.d])
      : gefiltert.map((r) => SPALTEN.map(({ k }) => (k === "letzter" ? dDe(r[k]) : r[k])));
    const f = (v) => (v == null ? "" : typeof v === "number" ? v.toFixed(2).replace(".", ",") : `"${String(v).replace(/"/g, '""')}"`);
    const text = "﻿" + [kopf, ...z].map((r) => r.map(f).join(";")).join("\n");
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
    a.download = `Kontoanalyse ${von} bis ${bis}.csv`; a.click();
  };

  const kopfZelle = (s) => (
    <th key={s.k} className={s.zahl ? "zahl" : ""} onClick={() => setSort({ k: s.k, auf: sort.k === s.k ? !sort.auf : !s.zahl })}>
      {s.l}{sort.k === s.k ? (sort.auf ? " ▲" : " ▼") : ""}
    </th>
  );

  return (
    <div className="ka-seite">
      <div className="ka-kopf">
        <button className="ka-btn" onClick={() => navigate(-1)}>← Zurück</button>
        <div>
          <h1>Kontoanalyse</h1>
          <p className="ka-unter">Kontostände aus dem täglichen Abruf · {d ? `${d.konten.length} Konten, ${tage.length} Abruftage (${dDe(erster)} – ${dDe(letzter)})` : "lädt …"}</p>
        </div>
      </div>
      {fehler && <div className="ka-fehler">{fehler}</div>}

      <div className="ka-karte ka-steuerung">
        <div className="ka-zeile">
          <span className="ka-label">Zeitraum</span>
          {[["vortag", "Vortag → heute"], ["woche", "7 Tage"], ["30", "30 Tage"], ["monat", "dieser Monat"], ["vormonat", "Vormonat"], ["jahr", "dieses Jahr"], ["alles", "alles"]].map(([k, l]) => (
            <button key={k} className={`ka-chip ${schnell === k ? "aktiv" : ""}`} onClick={() => zeitraum(k)}>{l}</button>
          ))}
          <span className="ka-datum">
            <label>von <input type="date" value={von} min={erster} max={bis || letzter} onChange={(e) => { setVon(e.target.value); setSchnell(""); }} /></label>
            <label>bis <input type="date" value={bis} min={von || erster} max={letzter} onChange={(e) => { setBis(e.target.value); setSchnell(""); }} /></label>
          </span>
        </div>
        <div className="ka-zeile">
          <span className="ka-label">Status</span>
          {statusListe.map(([w, n]) => <button key={w} className={`ka-chip ${status.includes(w) ? "aktiv" : ""}`} onClick={() => umschalten(status, setStatus, w)}>{w} <i>{n}</i></button>)}
          {status.length > 0 && <button className="ka-link" onClick={() => setStatus([])}>alle</button>}
        </div>
        <div className="ka-zeile">
          <span className="ka-label">View</span>
          {viewListe.map(([w, n]) => <button key={w} className={`ka-chip ${views.includes(w) ? "aktiv" : ""}`} onClick={() => umschalten(views, setViews, w)}>{w} <i>{n}</i></button>)}
          {views.length > 0 && <button className="ka-link" onClick={() => setViews([])}>alle</button>}
          <input className="ka-suche" placeholder="Suchen … (!=aktiv, null)" title="!= heißt ungleich (z. B. !=Firma), null = leer" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>

      <div className="ka-kpis">
        <div className="ka-kpi"><span>Kontostand am {dDe(bis)}</span><b>{eur0(summe("bis"))}</b><small>{gefiltert.length} Konten</small></div>
        <div className="ka-kpi"><span>Kontostand am {dDe(von)}</span><b>{eur0(summe("von"))}</b><small>Ausgangsstand</small></div>
        <div className={`ka-kpi ${vz(summe("diff"))}`}><span>Differenz im Zeitraum</span><b>{summe("diff") > 0 ? "+" : ""}{eur0(summe("diff"))}</b><small>{dDe(von)} → {dDe(bis)}</small></div>
        <div className="ka-kpi minus"><span>Abflüsse</span><b>{eur0(summe("diff", ab))}</b><small>{ab.length} {ab.length === 1 ? "Konto" : "Konten"} gesunken</small></div>
        <div className="ka-kpi plus"><span>Zuflüsse</span><b>+{eur0(summe("diff", zu))}</b><small>{zu.length} {zu.length === 1 ? "Konto" : "Konten"} gestiegen</small></div>
      </div>

      <div className="ka-zwei">
        <div className="ka-karte">
          <h3>Verlauf Summe Kontostand <small>(Auswahl, {dDe(von)} – {dDe(bis)})</small></h3>
          <Verlauf punkte={verlauf} />
        </div>
        <div className="ka-karte">
          <h3>Nach View</h3>
          <table className="ka-views">
            <thead><tr><th>View</th><th className="zahl">Konten</th><th className="zahl">Stand {dDe(bis).slice(0, 6)}</th><th className="zahl">Differenz</th><th /></tr></thead>
            <tbody>
              {jeView.map((g) => (
                <tr key={g.view} className={views.includes(g.view) ? "gewaehlt" : ""} onClick={() => umschalten(views, setViews, g.view)} title="als Filter setzen / lösen">
                  <td>{g.view}</td><td className="zahl">{g.n}</td><td className="zahl">{eur0(g.bis)}</td>
                  <td className={`zahl ${vz(g.diff)}`}>{g.diff > 0 ? "+" : ""}{eur0(g.diff)}</td>
                  <td className="ka-balken-zelle"><div className={`ka-balken ${vz(g.diff)}`} style={{ width: `${(100 * Math.abs(g.diff)) / maxDiff}%` }} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="ka-karte">
        <div className="ka-zeile ka-tabkopf">
          <button className={`ka-chip ${ansicht === "konten" ? "aktiv" : ""}`} onClick={() => setAnsicht("konten")}>je Konto</button>
          <button className={`ka-chip ${ansicht === "tage" ? "aktiv" : ""}`} onClick={() => setAnsicht("tage")}>alle Abrufe im Zeitraum</button>
          <span className="ka-klein">{ansicht === "konten" ? `${gefiltert.length} von ${konten.length} Konten` : `${roh.length} Abrufe`}</span>
          {Object.values(colF).some(Boolean) && <button className="ka-link" onClick={() => setColF({})}>Spaltenfilter löschen</button>}
          <button className="ka-btn" style={{ marginLeft: "auto" }} onClick={csv}>⬇ CSV (Excel)</button>
        </div>
        {ansicht === "konten" ? (
          <div className="ka-tab-wrap">
            <table className="ka-tab">
              <thead>
                <tr>{SPALTEN.map(kopfZelle)}</tr>
                <tr className="ka-filter">{SPALTEN.map((s) => (
                  <th key={s.k}><input value={colF[s.k] || ""} placeholder={s.zahl ? "<0, !=0, null" : "Filter, !=…"} title="!= ungleich · null = leer · Zahlen: < > <= >= =" onChange={(e) => setColF({ ...colF, [s.k]: e.target.value })} /></th>
                ))}</tr>
              </thead>
              <tbody>
                {gefiltert.map((r) => (
                  <React.Fragment key={r.schluessel}>
                    <tr className={offen === r.schluessel ? "offen" : ""} onClick={() => setOffen(offen === r.schluessel ? null : r.schluessel)}>
                      {SPALTEN.map(({ k }) => {
                        if (k === "ktoname") return <td key={k}><b>{r.ktoname || "–"}</b></td>;
                        if (k === "kontostatus") return <td key={k}><span className={`ka-tag st-${(r.kontostatus || "").toLowerCase().replace(/[^a-z]/g, "")}`}>{r.kontostatus}</span></td>;
                        if (k === "diff") return <td key={k} className={`zahl ${vz(r.diff)}`}><b>{r.diff > 0 ? "+" : ""}{eur(r.diff)}</b></td>;
                        if (["von", "bis", "min"].includes(k)) return <td key={k} className="zahl">{eur(r[k])}</td>;
                        if (k === "iban") return <td key={k} className="mono">{r.iban}</td>;
                        if (k === "letzter") return <td key={k}>{dDe(r.letzter)}</td>;
                        return <td key={k} className="ka-text" title={r[k] || ""}>{r[k]}</td>;
                      })}
                    </tr>
                    {offen === r.schluessel && (
                      <tr className="ka-detail"><td colSpan={SPALTEN.length}>
                        <div className="ka-verlauf-konto">
                          {(jeKonto[r.schluessel] || []).filter((s) => s.t >= von && s.t <= bis).map((s, i, l) => {
                            const dd = i ? s.s - l[i - 1].s : null;
                            return <span key={s.t}><small>{dDe(s.t).slice(0, 6)}</small> {eur0(s.s)}{dd != null && Math.abs(dd) > 0.005 && <em className={vz(dd)}> {dd > 0 ? "+" : ""}{eur0(dd)}</em>}</span>;
                          })}
                        </div>
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
                {!gefiltert.length && <tr><td colSpan={SPALTEN.length} className="ka-leer">{d ? "Keine Konten für diese Auswahl." : "lädt …"}</td></tr>}
              </tbody>
              <tfoot><tr>
                {SPALTEN.map(({ k }, i) => i === 0 ? <td key={k}>Summe ({gefiltert.length} Konten)</td>
                  : ["von", "bis", "diff"].includes(k) ? <td key={k} className={`zahl ${k === "diff" ? vz(summe("diff")) : ""}`}>{k === "diff" && summe("diff") > 0 ? "+" : ""}{eur(summe(k))}</td>
                  : <td key={k} />)}
              </tr></tfoot>
            </table>
          </div>
        ) : (
          <div className="ka-tab-wrap">
            <table className="ka-tab">
              <thead><tr><th>Tag</th><th>Konto (Kurz)</th><th>Kontoname</th><th>IBAN</th><th>Status</th><th>View</th><th className="zahl">Kontostand</th><th className="zahl">Diff. zum Abruf davor</th></tr></thead>
              <tbody>
                {roh.slice(0, 3000).map((r, i) => (
                  <tr key={i}><td>{dDe(r.t)}</td><td><b>{r.ktoname}</b></td><td>{r.kontoname}</td><td className="mono">{r.iban}</td><td>{r.kontostatus}</td><td>{r.kontoview}</td>
                    <td className="zahl">{eur(r.s)}</td><td className={`zahl ${vz(r.d)}`}>{r.d > 0 ? "+" : ""}{eur(r.d)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="ka-klein ka-hinweis">
          Kontostand an einem Tag = letzter Abruf an oder vor diesem Tag. Differenz = Stand am Bis-Tag minus Stand am Von-Tag.
          Status und View je Konto = zuletzt bekannter Wert. In jedem Feld: „!=“ heißt ungleich („!=0“, „!=aktiv“), „null“ findet leere Werte („!=null“ = nicht leer), „=text“ = genau gleich; Zahlen zusätzlich mit „&lt;0“, „&gt;=1000“. Zeile anklicken = Tagesverlauf des Kontos.
        </div>
      </div>
    </div>
  );
}
